import { X } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";

import { cn } from "../lib/cn";

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Actions pinned to the bottom, above the safe area. */
  footer?: ReactNode;
}

/**
 * A panel that rises from the bottom of the screen.
 *
 * Bottom-anchored because that is where a thumb is. On a phone the sheet fills the lower
 * half; from `sm` up it becomes a centred dialog, since a sheet clinging to the bottom of
 * a large monitor is a long way from where the eye is.
 *
 * Rendered as a native `dialog` so the browser supplies the focus trap, the Escape
 * handler and the inert backdrop — all things a hand-rolled overlay gets subtly wrong.
 */
export function BottomSheet({ open, onClose, title, children, footer }: BottomSheetProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      aria-label={title}
      onCancel={(event) => {
        // Escape fires cancel; route it through onClose so React state stays the
        // source of truth rather than the DOM's own open flag.
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // A click on the dialog element itself is the backdrop — its children stop
        // propagation by being children.
        if (event.target === dialogRef.current) {
          onClose();
        }
      }}
      className={cn(
        "w-full max-w-full backdrop:bg-inverse-surface/40",
        // Phone: pinned to the bottom, rounded on top only.
        "mt-auto mb-0 rounded-t-sheet rounded-b-none",
        // Larger: a centred dialog.
        "sm:m-auto sm:max-w-form sm:rounded-xl",
        "bg-surface p-0 text-on-surface shadow-float",
      )}
    >
      <div className="flex max-h-[80dvh] flex-col">
        {/* The grab handle: the one mark that says this panel came up from below. */}
        <span aria-hidden className="mx-auto mt-3 h-1.25 w-11 shrink-0 rounded-full bg-surface-container-highest sm:hidden" />

        <header className="flex items-center justify-between gap-md px-5 pb-4 pt-4 sm:pt-5">
          <h2 className="text-sheet-title text-on-surface">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex size-touch items-center justify-center rounded-full bg-surface-container text-on-surface transition-colors hover:bg-surface-container-high"
          >
            <X className="size-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 pb-[calc(var(--spacing)*8.5+var(--safe-area-bottom))] sm:pb-5">
          {children}
        </div>

        {footer ? (
          // Padding clears the home indicator on a phone.
          <footer className="flex items-center gap-sm border-t border-outline-variant p-md pb-[calc(var(--spacing-md)+var(--safe-area-bottom))]">
            {footer}
          </footer>
        ) : null}
      </div>
    </dialog>
  );
}
